import { GetOnMobileButton } from '@gagovictor/shared-frontend-core/dist/shared/components/GetOnMobile';
import { PwaInstallPrompt } from '@gagovictor/shared-frontend-core/dist/shared/components/PwaInstallPrompt';
import FavoriteRoundedIcon from '@mui/icons-material/FavoriteRounded';
import {
    AppBar,
    Box,
    CssBaseline,
    IconButton,
    ThemeProvider,
    Toolbar,
    Tooltip,
    Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import {
    BrowserRouter,
    Link,
    Route,
    Routes,
    useLocation,
} from 'react-router-dom';

import { getTheme } from './config/theme';
import { I18nProvider, useI18n } from './i18n';
import { LocaleGate } from './i18n/LocaleGate';
import { useAdFree } from './monetization/adFree';
import { AdsProvider } from './monetization/AdsProvider';
import { AppFooterAd } from './monetization/AppFooterAd';
import { config, isNative, webFooterAdConfigured } from './monetization/config';
import { NativeBanner } from './monetization/NativeBanner';
import {
    CardIndexPage,
    CardPage,
    ChironSignPage,
    LandingPage,
} from './seo/SeoPages';
import { CARD_PATH, LANDING } from './seo/slugs';
import { SleepSoundsPage } from './sleep/SleepSoundsPage';
import { RootState } from './store';
import { AboutPage } from './tarot/AboutPage';
import { BirthChartPage } from './tarot/BirthChartPage';
import { BreathingPage } from './tarot/BreathingPage';
import { SettingsMenu } from './tarot/components/SettingsMenu';
import { SupportDialog } from './tarot/components/SupportDialog';
import { CosmicCanvas } from './tarot/CosmicCanvas';
import { playCue } from './tarot/feedback';
import { HistoryPage } from './tarot/HistoryPage';
import { JournalPage } from './tarot/JournalPage';
import { HomePage } from './tarot/HomePage';
import { useReminders } from './tarot/hooks';
import { PrivacyPage } from './tarot/PrivacyPage';
import { ReadingPage } from './tarot/ReadingPage';
import { RitualPage } from './tarot/RitualPage';
import { sound } from './tarot/sound';

const Shell: React.FC = () => {
    const { t } = useI18n();
    const [supporting, setSupporting] = useState(false);
    const muted = useSelector((s: RootState) => s.tarot.muted);
    const adFree = useAdFree();
    // The sleep sounds cover the whole screen; an ad must not load hidden underneath them.
    const { pathname } = useLocation();
    const showFooter =
        !isNative() &&
        !adFree &&
        webFooterAdConfigured() &&
        pathname !== '/sleep';

    useEffect(() => sound.setMuted(muted), [muted]);
    useReminders();

    return (
        // A fixed wrapper, not a height inherited from #root: the AdSense script writes
        // `height: auto !important` onto the ancestors of its <ins>, which collapsed the page.
        <Box
            sx={{
                position: 'fixed',
                inset: 0,
                // Keeps the header and the bottom clear of the system bars (see MainActivity.java)
                // and of the native banner, which reports its own height.
                pt: 'var(--inset-top, env(safe-area-inset-top, 0px))',
                pr: 'var(--inset-right, env(safe-area-inset-right, 0px))',
                pb: 'calc(var(--inset-bottom, env(safe-area-inset-bottom, 0px)) + var(--banner-height, 0px))',
                pl: 'var(--inset-left, env(safe-area-inset-left, 0px))',
                boxSizing: 'border-box',
                display: 'flex',
                flexDirection: 'column',
            }}
        >
            <AppBar
                position="static"
                color="transparent"
                elevation={0}
                sx={{
                    position: 'relative',
                    zIndex: 2,
                    border: 0,
                    borderBottom: '1px solid rgba(212,175,106,.22)',
                }}
            >
                <Toolbar
                    sx={{
                        gap: 0.5,
                        px: { xs: 1.5, sm: 2 },
                        // The mobile button gets the paper fill Tomato Pop gives its header buttons.
                        '& .MuiButton-root': {
                            bgcolor: (th) =>
                                alpha(th.palette.background.paper, 0.9),
                            boxShadow: 1,
                            px: 1.75,
                            '&:hover': { bgcolor: 'background.paper' },
                        },
                    }}
                >
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography
                            variant="h6"
                            component={Link}
                            to="/"
                            onClick={() => playCue('ui')}
                            noWrap
                            sx={{
                                display: 'block',
                                color: 'primary.main',
                                textDecoration: 'none',
                                letterSpacing: '0.45em',
                                textShadow: '0 0 16px rgba(212,175,106,.5)',
                            }}
                        >
                            ✦ {t('app.name').toUpperCase()}
                        </Typography>
                    </Box>
                    <GetOnMobileButton
                        appName={t('app.name')}
                        description={t('app.getOnMobile')}
                        labels={{
                            button: t('app.getOnMobileButton'),
                            title: (name) =>
                                t('app.getOnMobileTitle', { name }),
                            close: t('app.close'),
                            googlePlay: t('store.googlePlay'),
                            appStore: t('store.appStore'),
                            comingSoon: t('store.comingSoon'),
                            comingSoonGooglePlay: t(
                                'store.comingSoonGooglePlay'
                            ),
                            comingSoonAppStore: t('store.comingSoonAppStore'),
                        }}
                        packageName={config.androidPackage}
                        appStoreId={config.iosAppId}
                        androidStatus={
                            config.androidLive ? 'active' : 'comingSoon'
                        }
                        iosStatus={config.iosAppId ? 'active' : 'comingSoon'}
                    />
                    <Tooltip title={t('app.support')}>
                        <IconButton
                            color="inherit"
                            aria-label={t('app.supportAria')}
                            onClick={() => {
                                playCue('ui');
                                setSupporting(true);
                            }}
                        >
                            <FavoriteRoundedIcon />
                        </IconButton>
                    </Tooltip>
                    <SettingsMenu />
                </Toolbar>
            </AppBar>
            <Routes>
                <Route path="/" element={<HomePage />} />
                <Route path="/ritual/:spread" element={<RitualPage />} />
                <Route path="/reading/:id" element={<ReadingPage />} />
                <Route path="/birth-chart" element={<BirthChartPage />} />
                <Route path="/birth-chart/:id" element={<BirthChartPage />} />
                {LANDING.map((id) => (
                    <Route
                        key={id}
                        path={`/${id}`}
                        element={<LandingPage id={id} />}
                    />
                ))}
                <Route path="/chiron-in/:sign" element={<ChironSignPage />} />
                <Route path={CARD_PATH} element={<CardIndexPage />} />
                <Route path={`${CARD_PATH}/:slug`} element={<CardPage />} />
                <Route path="/breathe" element={<BreathingPage />} />
                <Route path="/sleep" element={<SleepSoundsPage />} />
                <Route path="/history" element={<HistoryPage />} />
                <Route path="/journal" element={<JournalPage />} />
                <Route path="/privacy-policy" element={<PrivacyPage />} />
                <Route path="/about" element={<AboutPage />} />
                <Route path="*" element={<HomePage />} />
            </Routes>
            {showFooter && (
                <Box sx={{ position: 'relative', zIndex: 2 }}>
                    <AppFooterAd />
                </Box>
            )}
            <SupportDialog
                open={supporting}
                onClose={() => setSupporting(false)}
            />
        </Box>
    );
};

/** Not over the sleep sounds: it would cover their controls, and nobody is there to install anything. */
const InstallPrompt: React.FC = () => {
    const { pathname } = useLocation();
    return !isNative() && pathname !== '/sleep' ? <PwaInstallPrompt /> : null;
};

function App() {
    const theme = useMemo(() => getTheme(), []);

    return (
        <ThemeProvider theme={theme}>
            <CssBaseline />
            <CosmicCanvas />
            <I18nProvider>
                <LocaleGate>
                    <BrowserRouter>
                        <AdsProvider>
                            <Shell />
                            <NativeBanner />
                            <InstallPrompt />
                        </AdsProvider>
                    </BrowserRouter>
                </LocaleGate>
            </I18nProvider>
        </ThemeProvider>
    );
}

export default App;
